import { css, cx } from "@emotion/css";
import { BarsIcon } from "@octopusdeploy/design-system-icons";
import { space } from "@octopusdeploy/design-system-tokens";
import React from "react";
import { useMediaQuery } from "react-responsive";
import { useIsLargerThanIpadResolution } from "../../hooks/breakpoints";
import { resetStyles } from "../../utils/resetStyles";
import { NavigationBarIconButton } from "../NavigationBar/NavigationBarIconButton";
interface TopNavBarProps {
    logo?: React.ReactElement;
    searchBar?: React.ReactElement;
    /**
     * @deprecated Usage of this banner prop should consider using our in-app messaging tool first.
     */
    banner?: React.ReactElement;
    actions?: TopNavBarActionData[];
    onExpandMenu: () => void;
}
export interface TopNavBarActionData {
    key: string;
    content: React.ReactElement;
}
export function TopNavBar({ logo, searchBar, banner, actions, onExpandMenu }: TopNavBarProps) {
    const isLargerThanIpad = useIsLargerThanIpadResolution();
    const mobileBannerBreakpointWidth = 1231;
    const isMobileBannerScreen = useMediaQuery({ query: `(max-width:${mobileBannerBreakpointWidth}px)` });
    const inAppMessagingBannerTarget = <span data-chmln={"top-nav-banner"}/>;
    const actionsComponent = actions && <TopNavBarActions actions={actions}/>;
    // Desktop layout
    if (isLargerThanIpad) {
        // Desktop layout with banner on separate row
        if (isMobileBannerScreen) {
            return (<nav className={topNavBarStyles}>
                    <TopNavBarRow>
                        {logo}
                        {searchBar}
                        {actionsComponent}
                    </TopNavBarRow>
                    {banner}
                </nav>);
        }
        // Desktop layout with all elements on the same row
        return (<nav className={topNavBarStyles}>
                <TopNavBarRow>
                    {logo}
                    {searchBar}
                    {banner}
                    {inAppMessagingBannerTarget}
                    {actionsComponent}
                </TopNavBarRow>
            </nav>);
    }
    // Mobile layout with search bar and banner on separate rows
    return (<nav className={topNavBarStyles}>
            <TopNavBarRow>
                {<NavigationBarIconButton icon={<BarsIcon size={24}/>} accessibleName="Open navigation menu" onClick={onExpandMenu}/>}
                {logo}
                {actionsComponent}
            </TopNavBarRow>
            {searchBar && (<TopNavBarRow>
                    <div className={searchBarMobileContainerStyles}>{searchBar}</div>
                </TopNavBarRow>)}
            {banner}
        </nav>);
}
function TopNavBarRow({ children }: React.PropsWithChildren<{}>) {
    return <div className={cx(topNavBarRowStyles)}>{children}</div>;
}
function TopNavBarActions({ actions }: {
    actions?: TopNavBarActionData[];
}) {
    return (<div className={cx(topNavBarActionsContainerStyles)}>
            <ul className={actionsListStyles}>
                {actions?.map((action) => {
            if (!action.content) {
                return null;
            }
            return <li key={action.key}>{action.content}</li>;
        })}
            </ul>
        </div>);
}
const topNavBarStyles = css({
    position: "relative",
    padding: space[16],
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: space[16],
});
const topNavBarRowStyles = css({
    display: "flex",
    //We need an explicit height in order to avoid items/actions from expanding
    //the navbar past what the design accounts for.
    height: "2.25rem",
    gap: space[16],
});
const searchBarMobileContainerStyles = css({
    flex: 1,
});
const topNavBarActionsContainerStyles = css({
    marginLeft: "auto",
});
const actionsListStyles = css({
    ...resetStyles.list,
    display: "flex",
    alignItems: "center",
    gap: space[16],
});
